import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Paper, CircularProgress, Button } from '@mui/material';
import axiosInstance from '../api/axios';
import { useAuth } from '../context/AuthContext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const HydraChatPage: React.FC = () => {
  const [chatUrl, setChatUrl] = useState<string | null>(null);
  const [standaloneUrl, setStandaloneUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchChatUrls = async () => {
      if (!isAuthenticated) return;
      
      try {
        setLoading(true);
        
        // Fetch embedded URL
        const embeddedResponse = await axiosInstance.get('/api/hydra-chat-url?embedded=true');
        if (embeddedResponse.data && embeddedResponse.data.url) {
          setChatUrl(embeddedResponse.data.url);
        }
        
        // Fetch standalone URL
        const standaloneResponse = await axiosInstance.get('/api/hydra-chat-url?embedded=false');
        if (standaloneResponse.data && standaloneResponse.data.url) {
          setStandaloneUrl(standaloneResponse.data.url);
        }
        
        if (!embeddedResponse.data?.url && !standaloneResponse.data?.url) {
          setError('Failed to get chat URLs from server');
        }
      } catch (err) {
        console.error('Error fetching chat URLs:', err);
        setError('Error connecting to chat service. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchChatUrls();
  }, [isAuthenticated]);
  
  const openStandaloneChat = () => {
    if (standaloneUrl) {
      window.open(standaloneUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderChatContent = () => {
    if (!isAuthenticated) {
      return (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1">
            Please log in to access the chat.
          </Typography>
        </Paper>
      );
    }

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px" width="100%">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box>
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Having trouble with embedded chat?
          </Typography>
          <Typography variant="body1" paragraph>
            If you're experiencing issues with the embedded chat below, please try opening it in a new tab:
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<OpenInNewIcon />}
            onClick={openStandaloneChat}
            sx={{ mb: 2 }}
            disabled={!standaloneUrl}
          >
            Open Chat in New Tab
          </Button>
          
          <Typography variant="body2" color="text.secondary">
            Note: Due to browser security policies, the embedded chat might not work properly in some browsers.
          </Typography>
        </Paper>
        
        {chatUrl && (
          <Box 
            sx={{ 
              width: '100%', 
              height: '700px', 
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            <iframe
              src={chatUrl}
              title="AI Chat"
              width="100%"
              height="100%"
              style={{ 
                border: 'none',
                borderRadius: 'inherit'
              }}
              allow="camera; microphone"
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          AI Chat
        </Typography>
        <Typography variant="body1" paragraph>
          Hydra Chat AI Assistant
        </Typography>
      </Paper>
      
      <Box sx={{ mb: 4 }}>
        {renderChatContent()}
      </Box>
    </Container>
  );
};

export default HydraChatPage;